import { RecordingService, RecordingServiceOptions } from "globals/interfaces";
import Video, { LocalTrackPublication, LocalTrack, LocalVideoTrack } from "twilio-video";
import { Twilio } from "globals/enums";
import { handleError } from "helpers/sentry";
import { getTwilioVideoConstraintConfig } from "../config/constant";

export class TwilioService implements RecordingService {
  public twilioRoom: Video.Room;
  private options: RecordingServiceOptions = {};
  private networkLevel: number;
  private statsInterval: NodeJS.Timeout;
  private previousStats: { [trackSid: string]: { bytesSent: number; timestamp: number } } = {};

  async connect(options: RecordingServiceOptions) {
    this.options = options;
    const orgId = options.orgId || "";
    const constraintConfig = getTwilioVideoConstraintConfig(orgId);
    console.log("constraintConfig>>>>>>>>>>>>>>>", constraintConfig);

    // Use config values if enabled, else fallback to defaults
    const maxVideoBitrate = constraintConfig?.maxVideoBitrate ?? 1000000;
    console.log("maxVideoBitrate>>>>>>>>>>>>>>>", maxVideoBitrate);
    if (options.tracks) {
      const replaceWithClone = async (
        originalTrack: MediaStreamTrack,
        applyConstraints?: (track: MediaStreamTrack) => Promise<void>
      ) => {
        const trackIndex = options.tracks.indexOf(originalTrack);
        if (trackIndex === -1) {
  console.warn("Track not found in options.tracks, skipping clone");
  return;
}
        try {
          const clonedTrack = originalTrack.clone();
          if (applyConstraints) {
            await applyConstraints(clonedTrack);
          }
          options.tracks[trackIndex] = clonedTrack;
        } catch (error) {
          console.error("Failed to clone media track:", error);
        }
      };

      const videoTracks = options.tracks.filter((track) => track.kind === "video");
      for (const videoTrack of videoTracks) {
        await replaceWithClone(videoTrack, async (clonedTrack) => {
          if (constraintConfig) {
            await this.applyVideoConstraints(clonedTrack, constraintConfig);
          }
        });
      }

      const audioTracks = options.tracks.filter((track) => track.kind === "audio");
      for (const audioTrack of audioTracks) {
        await replaceWithClone(audioTrack);
      }
    }

    Video.connect(options.streamingToken, {
      audio: options.isAudio,
      tracks: options.tracks || [],
      maxVideoBitrate,
      networkQuality: {
        local: Twilio.NetworkQualityLocal,
        remote: Twilio.NetworkQualityRemote
      },
      automaticSubscription: false
    })
      .then((room) => {

        room.on("disconnected", (_, error) => {
          if (error) {
            handleError(`Twilio Disconnected: ${error}`);
          }
          clearInterval(this.statsInterval);
          this.options.onDisconnect();
        });

        room.on("reconnecting", () => {
          this.options.onReconnecting();
        });

        room.on("reconnected", () => {
          this.options.onReconnected();
        });

        room.localParticipant?.on("networkQualityLevelChanged", () => {
          const isGoodQuality = this.handleNetworkQualityChange(room);
          options.onNetworkQualityChange(isGoodQuality);
        });
        room.localParticipant.on("trackPublicationFailed", options.onConnectionFailure);
        this.networkLevel = room.localParticipant.networkQualityLevel;
        this.twilioRoom = room;
        this.options.trackPublished(room);

        // Set up detailed stats logging every 5 seconds
        clearInterval(this.statsInterval);
        this.statsInterval = setInterval(() => {
          room?.getStats()?.then((stats) => {
            if (stats && stats.length > 0) {
              const localVideoStats = stats[0]?.localVideoTrackStats || [];
              const localAudioStats = stats[0]?.localAudioTrackStats || [];
              const currentTime = Date.now();

              // Process video stats
              localVideoStats.forEach((stat) => {
                const trackSid = stat.trackSid || "unknown";
                const dimensions = `${stat.dimensions?.width || "N/A"}x${
                  stat.dimensions?.height || "N/A"
                }`;
                const fps = stat.frameRate || "N/A";
                const bytesSent = stat.bytesSent || 0;
                // const packetsSent = stat.packetsSent || 0;
                // const packetsLost = stat.packetsLost || 0;
                // const roundTripTime = stat.roundTripTime ? `${(stat.roundTripTime * 1000).toFixed(2)}ms` : "N/A";
                // const jitter = stat.jitter ? `${(stat.jitter * 1000).toFixed(2)}ms` : "N/A";

                // Calculate bandwidth in kbps based on difference from previous reading
                let bandwidthKbps = "0.00";
                if (this.previousStats[trackSid]) {
                  const bytesDiff = bytesSent - this.previousStats[trackSid].bytesSent;
                  const timeDiffSeconds =
                    (currentTime - this.previousStats[trackSid].timestamp) / 1000;
                  if (timeDiffSeconds > 0) {
                    bandwidthKbps = ((bytesDiff * 8) / (timeDiffSeconds * 1000)).toFixed(2);
                  }
                }

                // Store current stats for next calculation
                this.previousStats[trackSid] = {
                  bytesSent: bytesSent,
                  timestamp: currentTime
                };

                console.log(`[Twilio Video Stats] Track SID: ${trackSid}`);
                console.log(`  - Dimensions: ${dimensions}`);
                console.log(`  - FPS: ${fps}`);
                console.log(`  - Bandwidth: ${bandwidthKbps} kbps`);
                // console.log(`  - Network: Packets sent=${packetsSent}, lost=${packetsLost}, RTT=${roundTripTime}, Jitter=${jitter}`);

                // Log if it's a camera or screen track based on track SID pattern
                // Since we can't directly determine the track type from stats,
                // we'll check if the track is associated with screen sharing based on other properties
                if (stat.ssrc && stat.ssrc.toString().includes("screen")) {
                  console.log("  - Type: Likely screen video");
                } else {
                  console.log("  - Type: Likely camera video");
                }
              });

              // Process audio stats if needed
              if (localAudioStats.length > 0) {
                const audioStat = localAudioStats[0];
                const bytesSent = audioStat.bytesSent || 0;
                const audioTrackSid = audioStat.trackSid || "unknown-audio";

                // Calculate audio bandwidth in kbps based on difference from previous reading
                let audioBandwidthKbps = "0.00";
                if (this.previousStats[audioTrackSid]) {
                  const bytesDiff = bytesSent - this.previousStats[audioTrackSid].bytesSent;
                  const timeDiffSeconds =
                    (currentTime - this.previousStats[audioTrackSid].timestamp) / 1000;
                  if (timeDiffSeconds > 0) {
                    audioBandwidthKbps = ((bytesDiff * 8) / (timeDiffSeconds * 1000)).toFixed(2);
                  }
                }

                // Store current audio stats for next calculation
                this.previousStats[audioTrackSid] = {
                  bytesSent: bytesSent,
                  timestamp: currentTime
                };

                console.log("[Twilio Audio Stats]");
                console.log(`  - Bandwidth: ${audioBandwidthKbps} kbps`);
              }
            }
          });
        }, 5000);

        room.localParticipant.on("trackPublished", (track) => {
          console.log("Track Published", track);
          // Log the track's dimensions and constraints
          if (track.kind === "video") {
            const videoTrack = track.track;
            // Check if it's a LocalVideoTrack which has mediaStreamTrack property
            if (videoTrack && "mediaStreamTrack" in videoTrack) {
              const mediaStreamTrack = (videoTrack as any).mediaStreamTrack;
              if (mediaStreamTrack && mediaStreamTrack.getSettings) {
                const settings = mediaStreamTrack.getSettings();
                console.log("Published Video Track Settings:", {
                  width: settings.width,
                  height: settings.height,
                  frameRate: settings.frameRate,
                  trackName: track.trackName
                });
              }
            }
          }
        });
        room.localParticipant.on("recordingStarted", () => {
          console.log("Recording Started");
        });

        console.log("connected", room.localParticipant);
      })
      .catch((e) => {
        clearInterval(this.statsInterval);
        options.onConnectionFailure(e);
      });
  }

  handleNetworkQualityChange = (connect: Video.Room) => {
    const localNetworkQuality = connect.localParticipant?.networkQualityLevel;

    if (localNetworkQuality !== undefined) {
      const isBelowThreshold = localNetworkQuality < Twilio.NetworkQualityLevelThreshold;
      const isAboveThreshold = localNetworkQuality >= Twilio.NetworkQualityLevelThreshold;
      const currentAboveThreshold = this.networkLevel >= Twilio.NetworkQualityLevelThreshold;

      if (
        (isBelowThreshold && currentAboveThreshold) ||
        (isAboveThreshold && !currentAboveThreshold)
      ) {
        this.networkLevel = localNetworkQuality;
        return isAboveThreshold;
      }
    }

    return false;
  };

  private stopLocalTrack(track?: LocalTrack | null) {
    const stoppableTrack = track as { stop?: () => void } | null | undefined;
    stoppableTrack?.stop?.();
  }

  disconnect(): void {
    if (this.twilioRoom) {
      console.log("Twilio Disconnected");
      try {
        this.twilioRoom.localParticipant?.tracks?.forEach(
          (publication: LocalTrackPublication) => {
            try {
              publication.unpublish();
            } catch (error) {
              console.error("Failed to unpublish Twilio track", error);
            }
            try {
              this.stopLocalTrack(publication.track);
            } catch (error) {
              console.error("Failed to stop Twilio track", error);
            }
          }
        );
      } catch (error) {
        console.error("Failed to cleanup Twilio tracks", error);
      }
      this.twilioRoom.disconnect();
      this.twilioRoom = null;
    }
  }

  createLocalVideoTrack(track: MediaStreamTrack, trackName: string): LocalVideoTrack | null {
    try {
      // Skip cloning and constraints for camera-grab and screen-grab tracks
      // These are used for local capture at high resolution
      if (trackName === "camera-grab" || trackName === "screen-grab") {
        return new LocalVideoTrack(track, { name: trackName });
      }

      // For all other tracks (including screenshare), clone and apply constraints
      const clonedTrack = track.clone();

      // Fetch orgId from options or class property
      const orgId = this.options?.orgId || "";
      const constraintConfig = getTwilioVideoConstraintConfig(orgId);

      // Only apply constraints if configuration is defined
      if (constraintConfig) {
        if (trackName === "screenshare") {
          // For screen sharing, apply constraints to maintain aspect ratio but reduce quality
          this.applyScreenShareConstraints(clonedTrack, constraintConfig);
        } else {
          // For camera tracks, apply standard video constraints
          this.applyVideoConstraints(clonedTrack, constraintConfig);
        }
      }

      // Create local track with standard options
      return new LocalVideoTrack(clonedTrack, {
        name: trackName
        // Removed priority property as it's not supported in LocalTrackOptions
      });
    } catch (error) {
      console.error("Failed to create local video track:", error);
      return null;
    }
  }

  // Helper method to apply screen share constraints
  private async applyScreenShareConstraints(
    track: MediaStreamTrack,
    constraintConfig?: any
  ): Promise<void> {
    if (track.kind !== "video") return;

    // Get current settings to maintain aspect ratio
    const settings = track.getSettings();
    const currentWidth = settings.width || 1280;
    const currentHeight = settings.height || 720;

    // Calculate new dimensions that maintain aspect ratio but are much smaller
    // Target around 320px width which should help keep bitrate low
    const aspectRatio = currentWidth / currentHeight;
    const newWidth = constraintConfig?.screen?.width ?? 320;
    const newHeight = Math.round(newWidth / aspectRatio);

    // Apply constraints to significantly reduce resolution while maintaining aspect ratio
    const constraints = {
      width: { exact: newWidth },
      height: { exact: newHeight },
      frameRate: { max: constraintConfig?.screen?.frameRate ?? 3 } // Reduce frame rate for screen sharing to further save bandwidth
    };

    try {
      await track.applyConstraints(constraints);
      console.log("Applied screen share constraints:", constraints);
      // Log the actual settings after constraints are applied
      if (track.getSettings) {
        const newSettings = track.getSettings();
        console.log("Actual screen share settings after constraints:", {
          width: newSettings.width,
          height: newSettings.height,
          frameRate: newSettings.frameRate
        });
      }
    } catch (error) {
      console.error("Error applying screen share constraints:", error);
      // Try with less strict constraints
      try {
        const fallbackConstraints = {
          width: { ideal: newWidth },
          height: { ideal: newHeight },
          frameRate: { ideal: constraintConfig?.screen?.frameRate ?? 3 }
        };
        await track.applyConstraints(fallbackConstraints);
        console.log("Applied fallback screen share constraints:", fallbackConstraints);
      } catch (fallbackError) {
        console.error("Error applying fallback screen share constraints:", fallbackError);
      }
    }
  }

  async publishTrack(track: LocalVideoTrack) {
    if (track) {
      try {
        return await this.twilioRoom?.localParticipant?.publishTrack(track);
      } catch (e) {
        this.options.onConnectionFailure(e);
        return null;
      }
    }
  }

  unpublishTrack(publishedTrack: LocalTrackPublication) {
    if (publishedTrack) {
      try {
        publishedTrack.unpublish();
      } catch (error) {
        console.error("Failed to unpublish Twilio track", error);
      }
      try {
        this.stopLocalTrack(publishedTrack.track);
      } catch (error) {
        console.error("Failed to stop Twilio track on unpublish", error);
      }
    }
  }

  async flipVideo(tracks: MediaStreamTrack[]) {
    if (!tracks?.length) {
      return;
    }
    try {
      this.twilioRoom.localParticipant.videoTracks.forEach((publication) => {
        if (publication.track && publication.track.name !== "screenshare") {
          publication.unpublish();
          this.stopLocalTrack(publication.track);
        }
      });
      this.twilioRoom.localParticipant.audioTracks.forEach((publication) => {
        if (publication.track) {
          publication.unpublish();
          this.stopLocalTrack(publication.track);
        }
      });

      const newVideoTrack = tracks.find((track) => track.kind === "video");
      if (!newVideoTrack) {
        throw new Error("No video track found");
      }
      const clonedVideoTrack = newVideoTrack.clone();
      await this.twilioRoom.localParticipant?.publishTrack(clonedVideoTrack);

      const newAudioTrack = tracks.find((track) => track.kind === "audio");
      if (!newAudioTrack && this.options.isAudio) {
        throw new Error("No audio track found");
      }

      if (this.options.isAudio && newAudioTrack) {
        const clonedAudioTrack = newAudioTrack.clone();
        await this.twilioRoom.localParticipant?.publishTrack(clonedAudioTrack);
      }

      this.options.tracks = this.options.isAudio && newAudioTrack
        ? [newVideoTrack, newAudioTrack]
        : [newVideoTrack];
    } catch (error) {
      handleError(`Twilio Disconnected: ${error}`);
      this.options.onConnectionFailure(error);
    }
  }

  // Helper method to apply video constraints directly to a MediaStreamTrack
  private async applyVideoConstraints(
    track: MediaStreamTrack,
    constraintConfig?: any
  ): Promise<void> {
    if (track.kind !== "video") return;

    // Skip applying constraints to tracks used for camera grab or screen grab
    // This check assumes that the track's label or id contains information about its purpose
    // You may need to adjust this logic based on how tracks are identified in your application
    if (
      track.label &&
      (track.label.includes("camera-grab") || track.label.includes("screen-grab"))
    ) {
      console.log("Skipping constraints for camera/screen grab track:", track.label);
      return;
    }

    // Check if this is a screen sharing track
    const isScreenShare = track.label && track.label.includes("screenshare");
    if (isScreenShare) {
      if (constraintConfig) {
        return this.applyScreenShareConstraints(track, constraintConfig);
      }
    }

    // Get current settings to maintain aspect ratio
    const settings = track.getSettings();
    const currentWidth = settings.width || 640;
    const currentHeight = settings.height || 480;
    // Calculate new dimensions that maintain aspect ratio with max width 320px
    const aspectRatio = currentWidth / currentHeight;
    const newWidth = constraintConfig?.camera?.width ?? 320;
    const newHeight = Math.round(newWidth / aspectRatio);
    const frameRate = constraintConfig?.camera?.frameRate ?? 5;
    const constraints = {
      width: { ideal: newWidth },
      height: { ideal: newHeight },
      frameRate: { ideal: frameRate }
    };

    try {
      await track.applyConstraints(constraints);
      console.log("Applied video constraints successfully:", constraints);
      // Additional logging to verify the constraints were applied
      if (track.getSettings) {
        const settings = track.getSettings();
        console.log("Actual track settings after constraints:", {
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate
        });
      }
    } catch (error) {
      console.error("Error applying video constraints:", error);
      // Try with less strict constraints
      try {
        const fallbackConstraints = {
          width: { ideal: newWidth },
          height: { ideal: newHeight },
          frameRate: { ideal: frameRate }
        };
        await track.applyConstraints(fallbackConstraints);
        console.log("Applied fallback video constraints:", fallbackConstraints);
      } catch (fallbackError) {
        console.error("Error applying fallback constraints:", fallbackError);
      }
    }
  }
}
